import { Injectable, OnDestroy } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { EMPTY, Subscription, timer } from 'rxjs';
import { catchError, switchMap, timeout } from 'rxjs/operators';
import { AppSettings } from '../../app.settings';

interface IntercomIdentity { appId: string; token: string; }

/** MM-6038: only the authenticated backend supplies Messenger identity. */
@Injectable({providedIn: 'root'})
export class IntercomService implements OnDestroy {
  private readonly http: HttpClient;
  private token: string | null | undefined;
  private endpoint = '';
  private generation = 0;
  private renewal?: Subscription;

  constructor(backend: HttpBackend) {
    // Explicit credentials, without global interceptors replaying a different session
    // or logging the user out because a support-chat request failed.
    this.http = new HttpClient(backend);
  }

  setSession(token: string | null): void {
    const endpoint = AppSettings.getEndpoint().replace(/\/?$/, '/');
    if (this.token === token && this.endpoint === endpoint) return;
    this.token = token;
    this.endpoint = endpoint;
    const generation = ++this.generation;
    this.renewal?.unsubscribe();
    void this.shutdown().then(cleared => {
      if (!cleared || generation !== this.generation) return;
      if (!token) {
        // Visitor chat is only for signed-out users.
        this.boot({});
        return;
      }
      this.renewal = timer(0, 240_000).pipe(
        switchMap(() => this.http.post<IntercomIdentity>(`${endpoint}auth/intercom-token`, {}, {
          headers: {Authorization: `Bearer ${token}`},
        }).pipe(timeout(10_000))),
        catchError(() => {
          if (generation === this.generation) this.fail();
          return EMPTY;
        }),
      ).subscribe(identity => {
        if (generation !== this.generation) return;
        if (identity?.appId !== 'zxmxrc5i' || typeof identity.token !== 'string' || !identity.token.trim()) {
          this.renewal?.unsubscribe();
          this.fail();
          return;
        }
        this.boot({intercom_user_jwt: identity.token, session_duration: 300_000});
      });
    });
  }

  private boot(identity: Record<string, string | number>): void {
    const sdk = (window as any).Intercom;
    if (typeof sdk !== 'function') return;
    sdk('boot', {...(window as any).wallitIntercomSettings, app_id: 'zxmxrc5i', ...identity});
  }

  private fail(): void {
    void this.shutdown();
    console.error('Support chat could not be securely initialized.');
  }

  private async shutdown(): Promise<boolean> {
    try {
      const sdk = (window as any).Intercom;
      if (typeof sdk === 'function') sdk('shutdown');
      // The SDK saves composer drafts asynchronously while unmounting.
      await new Promise<void>(resolve => setTimeout(resolve, 0));
      const storage = window.sessionStorage;
      for (let index = storage.length - 1; index >= 0; index--) {
        const key = storage.key(index);
        if (key === 'intercom.draft' || key?.startsWith('intercom.draft-')) storage.removeItem(key);
      }
      return true;
    } catch {
      console.error('Support chat could not safely clear the previous session.');
      return false;
    }
  }

  ngOnDestroy(): void {
    this.generation++;
    this.renewal?.unsubscribe();
    void this.shutdown();
  }
}
